import type { TOC } from '@ember/component/template-only';
import { uniqueId } from '@ember/helper';
import { PhChecks } from 'ember-phosphor-icons';
import EmptyState from 'tio-ui/components/empty-state';

interface AttentionPanelSignature {
  Args: {
    /** Section heading, e.g. "Needs Your Attention"; an h2 that also names the region. */
    title: string;
    /**
     * Number of rows the caller yields. Above 0 it shows as "(n)" after the
     * title and the rows render; at 0 the empty state renders instead and any
     * yielded rows are ignored.
     */
    count: number;
    /** Empty-state heading, shown when `@count` is 0. */
    emptyTitle: string;
    /** Empty-state supporting line, shown under `@emptyTitle`. */
    emptyBody?: string;
  };
  Blocks: {
    /** `TaskCard` rows, already in urgency order (see `sortByDeadline`). */
    default?: [];
  };
  Element: HTMLElement;
}

/**
 * Figma "Needs Your Attention" container: a sunken panel with a counted
 * heading and a list of task rows. It always renders; with nothing to show it
 * holds an empty state, so the user can tell nothing is waiting on them.
 */
const AttentionPanel: TOC<AttentionPanelSignature> = <template>
  {{#let (uniqueId) as |headingId|}}
    <section
      class="tio-col rounded-2xl tio-bg-sunken p-4"
      aria-labelledby={{headingId}}
      ...attributes
    >
      <div class="tio-row-tight items-baseline">
        <h2 id={{headingId}}>{{@title}}</h2>
        {{#if @count}}
          <span class="tio-text-base-medium tio-fg-secondary">({{@count}})</span>
        {{/if}}
      </div>
      {{#if @count}}
        <ul class="tio-col gap-2" role="list">
          {{yield}}
        </ul>
      {{else}}
        <EmptyState
          @icon={{PhChecks}}
          @title={{@emptyTitle}}
          @description={{@emptyBody}}
          @compact={{true}}
        />
      {{/if}}
    </section>
  {{/let}}
</template>;

export default AttentionPanel;
